// ============================================================================
// CLIENT REGISTRATION FORM MODAL
// ============================================================================
// Add / Edit form for one statutory registration held by a client
// (PAN / GSTIN / CIN / TAN / BURS-VAT / …).
//
// Follows the prima-web form-modal blueprint (see CLAUDE.md):
//  - Internal formData state, initial snapshot for dirty tracking
//  - Inline field validation via fieldErrors + getFieldError + clearFieldError
//  - Save flow: validate → ConfirmDialog → loading overlay → parent toast
//  - Discard-changes ConfirmDialog on close when isDirty
//
// It deliberately does NOT support minimize-to-bar: it opens on top of the
// Client Registrations drill-down, which is itself a modal over the client
// grid. Minimizing the inner form would leave the outer list stranded.
//
// THE FORMAT HINT IS THE SAME RULE THE SERVER ENFORCES
//
// Every registration type may carry a `formatPattern`. The server rejects a
// number that does not match it (ClientRegistrationService), and this form
// applies the identical regex as you type so the rejection arrives before the
// round trip rather than after it. When a type has no pattern the field simply
// accepts anything — that is how a new jurisdiction is onboarded before its
// format is known.

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import type { RegistrationType } from 'api/types';

export interface ClientRegistrationFormData {
  registrationTypeId: string;
  registrationNumber: string;
  jurisdiction: string;
  registrationDate: string;
  validFrom: string;
  validTo: string;
  renewalReminderDays: string;
  issuingAuthority: string;
  remarks: string;
  statusCode: 'ACTIVE' | 'INACTIVE';
}

export interface ClientRegistrationFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /** Client the registration belongs to — shown in the header for context. */
  clientName?: string | null;
  /** Active registration types, already ordered country → displayOrder → code. */
  registrationTypes: RegistrationType[];
  registrationTypesLoading?: boolean;
  /** Existing record (edit mode). Null/undefined in create mode. */
  initialData?: ClientRegistrationFormData | null;
  isSaving?: boolean;
  apiError?: string | null;
  onSave: (data: ClientRegistrationFormData) => void;
  onClose: () => void;
}

const INITIAL_FORM_DATA: ClientRegistrationFormData = {
  registrationTypeId: '',
  registrationNumber: '',
  jurisdiction: '',
  registrationDate: '',
  validFrom: '',
  validTo: '',
  renewalReminderDays: '',
  issuingAuthority: '',
  remarks: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export const ClientRegistrationFormModal: React.FC<ClientRegistrationFormModalProps> = ({
  isOpen,
  mode,
  clientName,
  registrationTypes,
  registrationTypesLoading = false,
  initialData,
  isSaving = false,
  apiError,
  onSave,
  onClose,
}) => {
  const [formData, setFormData] = useState<ClientRegistrationFormData>(INITIAL_FORM_DATA);
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const initialSnapshotRef = useRef('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // --- Populate / reset form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const src = initialDataRef.current;
    const data: ClientRegistrationFormData = src ? { ...src } : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const setField = useCallback((field: keyof ClientRegistrationFormData, value: string) => {
    clearFieldError(field as string);
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, [clearFieldError]);

  // --- The chosen type drives the number's format rule and default authority ---
  const selectedType = useMemo(
    () => registrationTypes.find((t) => t.id === formData.registrationTypeId) ?? null,
    [registrationTypes, formData.registrationTypeId]
  );

  const typeOptions = useMemo(
    () =>
      registrationTypes.map((t) => ({
        value: t.id,
        // Ordered country → displayOrder → code by the server, so prefixing the
        // country makes the list read as jurisdiction groups without needing
        // option grouping the select does not support.
        label: `${t.countryCode ?? 'Any'} · ${t.code} — ${t.name}`,
      })),
    [registrationTypes]
  );

  /**
   * Live format check against the selected type's pattern — the same regex the
   * server applies. An uncompilable pattern is treated as no pattern, matching
   * the server's behaviour rather than blocking the field.
   */
  const formatMismatch = useMemo(() => {
    const pattern = selectedType?.formatPattern;
    const value = formData.registrationNumber.trim().toUpperCase();
    if (!pattern || !value) return false;
    try {
      return !new RegExp(pattern).test(value);
    } catch {
      return false;
    }
  }, [selectedType, formData.registrationNumber]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose, isSaving]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const number = formData.registrationNumber.trim().toUpperCase();

    if (!formData.registrationTypeId) {
      errors.registrationTypeId = 'Registration type is required';
    }

    if (!number) {
      errors.registrationNumber = 'Registration number is required';
    } else if (number.length > 50) {
      errors.registrationNumber = 'Registration number must be 50 characters or less';
    } else if (formatMismatch && selectedType) {
      errors.registrationNumber =
        `Not a valid ${selectedType.code}. Expected format: ${selectedType.formatPattern}`;
    }

    if (formData.validFrom && formData.validTo && formData.validTo < formData.validFrom) {
      errors.validTo = 'Valid to cannot be earlier than valid from';
    }

    if (formData.renewalReminderDays) {
      const days = Number(formData.renewalReminderDays);
      if (!Number.isInteger(days) || days < 0 || days > 3650) {
        errors.renewalReminderDays = 'Reminder must be a whole number between 0 and 3650 days';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input, textarea')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, formatMismatch, selectedType]);

  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave({
      ...formData,
      registrationNumber: formData.registrationNumber.trim().toUpperCase(),
      jurisdiction: formData.jurisdiction.trim(),
      issuingAuthority: formData.issuingAuthority.trim(),
      remarks: formData.remarks.trim(),
    });
  }, [formData, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add Registration' : 'Edit Registration'}
            {clientName ? ` — ${clientName}` : ''}
          </h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving} title="Close">
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {apiError && (
            <div
              style={{
                marginBottom: '0.75rem',
                padding: '0.625rem 0.75rem',
                background: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {apiError}
            </div>
          )}

          <div className="form-grid-2">
            {/* Registration type */}
            <div className="form-group" data-field="registrationTypeId">
              <SyutSelect
                label="Registration Type"
                required
                value={formData.registrationTypeId}
                options={typeOptions}
                isLoading={registrationTypesLoading}
                isDisabled={isSaving}
                isClearable={false}
                isSearchable
                size="sm"
                error={getFieldError('registrationTypeId')}
                placeholder="Select a registration type…"
                onChange={(val) => setField('registrationTypeId', (val as string) || '')}
              />
            </div>

            {/* Status */}
            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                value={formData.statusCode}
                options={STATUS_OPTIONS}
                isClearable={false}
                isDisabled={isSaving}
                size="sm"
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    statusCode: (val as 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
                  }))
                }
              />
            </div>
          </div>

          {/* Registration number */}
          <div className="form-group" data-field="registrationNumber">
            <label className="form-label">
              Registration Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('registrationNumber') ? ' form-field-error' : ''}`}
              value={formData.registrationNumber}
              maxLength={50}
              placeholder={selectedType ? `Enter the ${selectedType.code}` : 'Select a type first'}
              disabled={isSaving}
              onChange={(e) => setField('registrationNumber', e.target.value.toUpperCase())}
            />
            {getFieldError('registrationNumber') ? (
              <div className="form-error-message">{getFieldError('registrationNumber')}</div>
            ) : (
              selectedType?.formatPattern && (
                <div
                  style={{
                    marginTop: '0.25rem',
                    fontSize: '0.75rem',
                    color: formatMismatch ? '#dc2626' : 'var(--text-secondary)',
                  }}
                >
                  {formatMismatch
                    ? `Does not match the expected ${selectedType.code} format`
                    : `Format: ${selectedType.formatPattern}`}
                </div>
              )
            )}
          </div>

          <div className="form-grid-2">
            {/* Jurisdiction */}
            <div className="form-group" data-field="jurisdiction">
              <label className="form-label">Jurisdiction</label>
              <input
                type="text"
                className="form-input"
                value={formData.jurisdiction}
                maxLength={100}
                placeholder="State / authority / region"
                disabled={isSaving}
                onChange={(e) => setField('jurisdiction', e.target.value)}
              />
            </div>

            {/* Issuing authority */}
            <div className="form-group" data-field="issuingAuthority">
              <label className="form-label">Issuing Authority</label>
              <input
                type="text"
                className="form-input"
                value={formData.issuingAuthority}
                maxLength={200}
                placeholder={selectedType?.issuingAuthority || 'Defaults to the type’s authority'}
                disabled={isSaving}
                onChange={(e) => setField('issuingAuthority', e.target.value)}
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="registrationDate">
              <label className="form-label">Registration Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.registrationDate}
                disabled={isSaving}
                onChange={(e) => setField('registrationDate', e.target.value)}
              />
            </div>
            <div className="form-group" data-field="validFrom">
              <label className="form-label">Valid From</label>
              <input
                type="date"
                className="form-input"
                value={formData.validFrom}
                disabled={isSaving}
                onChange={(e) => setField('validFrom', e.target.value)}
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="validTo">
              <label className="form-label">Valid To</label>
              <input
                type="date"
                className={`form-input${getFieldError('validTo') ? ' form-field-error' : ''}`}
                value={formData.validTo}
                disabled={isSaving}
                onChange={(e) => setField('validTo', e.target.value)}
              />
              {getFieldError('validTo') ? (
                <div className="form-error-message">{getFieldError('validTo')}</div>
              ) : (
                <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Leave blank for an evergreen registration
                </div>
              )}
            </div>
            <div className="form-group" data-field="renewalReminderDays">
              <label className="form-label">Renewal Reminder (days)</label>
              <input
                type="number"
                min={0}
                max={3650}
                className={`form-input${getFieldError('renewalReminderDays') ? ' form-field-error' : ''}`}
                value={formData.renewalReminderDays}
                placeholder="e.g. 30"
                // Never disabled on "no Valid To": a row created outside this
                // form can hold a reminder without an expiry, and disabling the
                // field would leave that value stuck and unclearable.
                disabled={isSaving}
                onChange={(e) => setField('renewalReminderDays', e.target.value)}
              />
              {getFieldError('renewalReminderDays') ? (
                <div className="form-error-message">{getFieldError('renewalReminderDays')}</div>
              ) : (
                !formData.validTo &&
                formData.renewalReminderDays && (
                  <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#d97706' }}>
                    No effect without a Valid To date
                  </div>
                )
              )}
            </div>
          </div>

          <div className="form-group" data-field="remarks">
            <label className="form-label">Remarks</label>
            <textarea
              className="form-input"
              value={formData.remarks}
              rows={2}
              placeholder="Optional notes"
              disabled={isSaving}
              onChange={(e) => setField('remarks', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {mode === 'create' ? 'Add' : 'Save Changes'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'create' ? 'Confirm Add' : 'Confirm Update'}
        message={
          mode === 'create'
            ? `Add ${selectedType?.code ?? 'registration'} "${formData.registrationNumber.trim().toUpperCase()}"?`
            : `Save changes to "${formData.registrationNumber.trim().toUpperCase()}"?`
        }
        confirmLabel={mode === 'create' ? 'Add' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default ClientRegistrationFormModal;
